import { useEffect, useRef } from 'react';
import useAsyncRetry from 'react-use/lib/useAsyncRetry';
import { ErrorPanel, Progress } from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { ALLURE_PROJECT_ID_ANNOTATION } from '@backstage-community/plugin-allure';

// Ask for a new token this long before the current one expires.
const REFRESH_MARGIN_MS = 60_000;
const MIN_REFRESH_DELAY_MS = 30_000;

// `allow-same-origin` is deliberately absent: a script inside the report must
// not run with the portal's origin, cookies or storage. `allow-popups` lets
// report links open a new tab, which inherits the sandbox (never add
// `allow-popups-to-escape-sandbox`: an escaped popup could load report content
// as the portal's origin). The proxy answers report files with CORS so the
// opaque-origin report can still fetch its data (`allureProxyModule`).
export const ALLURE_IFRAME_SANDBOX = 'allow-scripts allow-popups';

/**
 * The entity "Allure Report" tab: the report of the `qameta.io/allure-project`
 * project in a sandboxed iframe. The iframe cannot carry the Backstage session,
 * so the backend issues a short-lived token that goes in the URL path.
 */
export function AllureReportContent() {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const projectId = entity.metadata.annotations?.[ALLURE_PROJECT_ID_ANNOTATION];

  const { value, error, retry } = useAsyncRetry(async () => {
    const proxyUrl = await discoveryApi.getBaseUrl('proxy');
    const response = await fetchApi.fetch(`${proxyUrl}/allure-token`);
    if (!response.ok) {
      throw new Error(
        `Could not get access to the Allure report (HTTP ${response.status})`,
      );
    }
    const { token, expiresAt } = await response.json();
    return { proxyUrl, token: token as string, expiresAt: expiresAt as string };
  }, [discoveryApi, fetchApi]);

  // Keep the last token that worked: a refresh in flight or a failed refresh
  // must not unmount a report that is still showing with a valid token. The
  // ref is written in an effect, which runs before the refresh effect below.
  const lastSession = useRef(value);
  useEffect(() => {
    if (value) {
      lastSession.current = value;
    }
  }, [value]);
  const session = value ?? lastSession.current;

  // The token lasts about an hour; fetch a new one before it ends so report
  // data requests keep working. Loading a new URL reloads the report. If a
  // refresh fails, try again shortly while the current token is still valid.
  useEffect(() => {
    let delay: number;
    if (value) {
      delay = Math.max(
        Date.parse(value.expiresAt) - Date.now() - REFRESH_MARGIN_MS,
        MIN_REFRESH_DELAY_MS,
      );
    } else if (error && lastSession.current) {
      delay = MIN_REFRESH_DELAY_MS;
    } else {
      return undefined;
    }
    const timer = setTimeout(retry, delay);
    return () => clearTimeout(timer);
  }, [value, error, retry]);

  if (!session) {
    return error ? <ErrorPanel error={error} /> : <Progress />;
  }
  if (!projectId) {
    return <Progress />;
  }

  const src =
    `${session.proxyUrl}/allure/t/${encodeURIComponent(session.token)}` +
    `/allure-docker-service/projects/${encodeURIComponent(projectId)}` +
    `/reports/latest/index.html`;

  return (
    <iframe
      title="Allure Report"
      sandbox={ALLURE_IFRAME_SANDBOX}
      // `src` holds a live token: keep it out of Datadog session replay.
      data-dd-privacy="hidden"
      src={src}
      style={{ display: 'table', width: '100%', height: '100%' }}
    />
  );
}
